import { makeStyles } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  container: {
    maxWidth: 980,
    margin: '0 auto',
    padding: theme.spacing(4, 3, 8),
  },
  title: {
    fontSize: '2rem',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(4),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
  },
  list: {
    margin: 0,
    paddingLeft: theme.spacing(3),
    color: theme.palette.text.primary,
    '& li': {
      marginBottom: theme.spacing(0.75),
    },
  },
  code: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    overflowX: 'auto',
    fontSize: '0.88rem',
    lineHeight: 1.5,
    margin: 0,
  },
  note: {
    color: theme.palette.text.secondary,
  },
}));

export const ProjectGuidePage = () => {
  const classes = useStyles();

  return (
    <main className={classes.container}>
      <h1 className={classes.title}>Backstage Portal Usage Guide</h1>
      <p className={classes.subtitle}>
        This page is for users after the project is already set up. Follow these steps to browse APIs and onboard new ones.
      </p>

      <section className={classes.section}>
        <h2 className={classes.sectionTitle}>Features</h2>
        <ul className={classes.list}>
          <li>Upload and manage API specifications (OpenAPI YAML/JSON)</li>
          <li>Visualize API endpoints and metadata</li>
          <li>Developer-friendly interface for API discovery</li>
          <li>Versioning and status tracking</li>
          <li>Tag-based organization</li>
          <li>Extensible for integrations with backend services</li>
        </ul>
      </section>

      <section className={classes.section}>
        <h2 className={classes.sectionTitle}>Start Here (User Flow)</h2>
        <ol className={classes.list}>
          <li>Sign in with your portal username and password.</li>
          <li>Use Home page cards to open API Catalog, Add New API, or this guide.</li>
          <li>Use Search to quickly find APIs by name, tags, or metadata.</li>
          <li>Open API Catalog to view existing APIs and inspect API details.</li>
        </ol>
      </section>

      <section className={classes.section}>
        <h2 className={classes.sectionTitle}>API Onboarding (Add New API)</h2>
        <ol className={classes.list}>
          <li>Click Add New API and open /catalog-import.</li>
          <li>Choose Register Existing API.</li>
          <li>Paste the URL to catalog-info.yaml from your Git repository.</li>
          <li>Click Analyze, review the detected entity, and click Import.</li>
          <li>Open API Catalog (/api-docs) and confirm the new API is listed.</li>
        </ol>
      </section>

      <section className={classes.section}>
        <h2 className={classes.sectionTitle}>Catalog Info Template</h2>
        <p className={classes.note}>Use this minimal catalog-info.yaml when onboarding a new API:</p>
        <pre className={classes.code}>{`apiVersion: backstage.io/v1alpha1
kind: API
metadata:
  name: my-new-api
  description: API for <business capability>
  tags:
    - acme
spec:
  type: openapi
  lifecycle: production
  owner: group:default/admins
  system: default/examples
  definition:
    $text: ./openapi/api.yaml`}</pre>
      </section>

      <section className={classes.section}>
        <h2 className={classes.sectionTitle}>Verify Your API</h2>
        <ul className={classes.list}>
          <li>API is visible in API Catalog (/api-docs).</li>
          <li>Name, owner, type, and lifecycle are correct.</li>
          <li>Specification renders correctly in API details.</li>
          <li>Raw YAML/JSON spec is accessible from the details page.</li>
        </ul>
      </section>

      <section className={classes.section}>
        <h2 className={classes.sectionTitle}>Common Issues</h2>
        <ul className={classes.list}>
          <li>The following location already exists: API source was already imported.</li>
          <li>Analyze fails: catalog-info.yaml URL is incorrect or inaccessible.</li>
          <li>Spec not loading: spec.definition path does not match repo file path.</li>
          <li>Wrong owner or lifecycle: update spec.owner and spec.lifecycle values.</li>
        </ul>
        <p className={classes.note}>
          Tip: keep one catalog-info.yaml per API and ensure paths are relative to that file.
        </p>
      </section>
    </main>
  );
};
